// src/admin/pages/SubscriptionPlansPage.js
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Calendar,
  Users,
  Crown,
  Shield,
  Zap
} from 'lucide-react';
import { supabase } from '../../config';

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form state for creating/editing plans
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    stripe_product_id: '',
    stripe_price_id_monthly: '',
    stripe_price_id_yearly: '',
    features: [],
    plan_type: 'basic', // free, trial, basic, pro, enterprise
    is_active: true,
    show_on_frontend: true,
    unique_token: '',
    trial_days: 0,
    sort_order: 0
  });

  const [newFeature, setNewFeature] = useState('');

  // Mock data for fallback - NOTE: Plan limits are stored in product_restrictions
  // eslint-disable-next-line no-unused-vars
  const mockPlans = [
    {
      id: 1,
      name: 'Free',
      description: 'Perfect for getting started',
      price_monthly: 0,
      price_yearly: 0,
      features: ['Up to 1 company', 'Basic equity tracking', 'Limited support'],
      plan_type: 'free',
      is_active: true,
      trial_days: 0,
      sort_order: 1
    },
    {
      id: 2,
      name: 'Basic',
      description: 'Essential tools for small businesses',
      price_monthly: 2900,
      price_yearly: 29000,
      features: ['Up to 5 companies', 'Full equity management', 'Basic dataroom', 'Email support'],
      plan_type: 'basic',
      is_active: true,
      trial_days: 14,
      sort_order: 2
    },
    {
      id: 3,
      name: 'Pro',
      description: 'Advanced features for growing companies',
      price_monthly: 7900,
      price_yearly: 79000,
      features: ['Unlimited companies', 'Advanced equity management', 'Full dataroom access', 'Capital raising tools', 'Priority support'],
      plan_type: 'pro',
      is_active: true,
      trial_days: 30,
      sort_order: 3
    },
    {
      id: 4,
      name: 'Enterprise',
      description: 'Custom solutions for large organizations',
      price_monthly: 0,
      price_yearly: 0,
      features: ['Everything in Pro', 'Custom integrations', 'Dedicated support', 'SLA guarantees', 'Custom branding'],
      plan_type: 'enterprise',
      is_active: true,
      trial_days: 0,
      sort_order: 4
    }
  ];

  useEffect(() => {
    fetchPlans();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlans = async () => {
    try {
      setLoading(true);
      
      // Query Supabase directly
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;

      setPlans(data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load subscription plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  // Convert plan name to URL-friendly format
  const slugifyPlanName = (planName) => {
    if (!planName) return '';
    return planName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  const generateUniqueToken = (planName = '') => {
    // Start with plan name slug if provided
    let token = '';
    if (planName) {
      const slug = slugifyPlanName(planName);
      token = slug ? `${slug}-` : '';
    }
    
    // Generate random suffix: lowercase, alphanumeric
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const suffixLength = 12; // Reduced since we have plan name prefix
    for (let i = 0; i < suffixLength; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  };

  const handleGenerateToken = () => {
    const planName = formData.name || '';
    const newToken = generateUniqueToken(planName);
    setFormData({ ...formData, unique_token: newToken });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Prepare plan data - only include fields that exist in the form
      const planData = {
        name: formData.name,
        description: formData.description || null,
        price_monthly: parseInt(formData.price_monthly) || 0,
        price_yearly: parseInt(formData.price_yearly) || 0,
        stripe_product_id: formData.stripe_product_id || null,
        stripe_price_id_monthly: formData.stripe_price_id_monthly || null,
        stripe_price_id_yearly: formData.stripe_price_id_yearly || null,
        plan_type: formData.plan_type,
        is_active: formData.is_active,
        show_on_frontend: formData.show_on_frontend,
        unique_token: formData.unique_token || null,
        trial_days: parseInt(formData.trial_days) || 0,
        sort_order: parseInt(formData.sort_order) || 0
      };

      // Clean up the data - ensure required fields are present and handle optional fields
      // Required fields: name, plan_type, price_monthly, price_yearly
      const finalPlanData = {
        name: planData.name,
        plan_type: planData.plan_type,
        price_monthly: planData.price_monthly,
        price_yearly: planData.price_yearly
      };
      
      // Add optional fields only if they have values
      if (planData.description) finalPlanData.description = planData.description;
      if (planData.stripe_product_id) finalPlanData.stripe_product_id = planData.stripe_product_id;
      if (planData.stripe_price_id_monthly) finalPlanData.stripe_price_id_monthly = planData.stripe_price_id_monthly;
      if (planData.stripe_price_id_yearly) finalPlanData.stripe_price_id_yearly = planData.stripe_price_id_yearly;
      if (planData.is_active !== undefined) finalPlanData.is_active = planData.is_active;
      if (planData.show_on_frontend !== undefined) finalPlanData.show_on_frontend = planData.show_on_frontend;
      if (planData.unique_token) finalPlanData.unique_token = planData.unique_token;
      if (planData.trial_days !== undefined) finalPlanData.trial_days = planData.trial_days;
      if (planData.sort_order !== undefined) finalPlanData.sort_order = planData.sort_order;

      if (editingPlan) {
        // Update existing plan - preserve product_restrictions and other fields
        const { error } = await supabase
          .from('subscription_plans')
          .update(finalPlanData)
          .eq('id', editingPlan.id);

        if (error) {
          console.error('Error updating plan:', error);
          throw error;
        }

        await fetchPlans();
        setMessage('Plan updated successfully!');
      } else {
        // Create new plan
        const { error } = await supabase
          .from('subscription_plans')
          .insert([finalPlanData])
          .select();

        if (error) {
          console.error('Error creating plan:', error);
          throw error;
        }

        await fetchPlans();
        setMessage('Plan created successfully!');
      }

      setShowCreateForm(false);
      setEditingPlan(null);
      resetForm();
    } catch (err) {
      setError(err.message || 'An error occurred while saving the plan.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price_monthly: '',
      price_yearly: '',
      stripe_product_id: '',
      stripe_price_id_monthly: '',
      stripe_price_id_yearly: '',
      features: [],
      plan_type: 'basic',
      is_active: true,
      show_on_frontend: true,
      unique_token: '',
      trial_days: 0,
      sort_order: 0
    });
  };

  const handleEdit = (plan) => {
    setFormData(plan);
    setEditingPlan(plan);
    setShowCreateForm(true);
  };

  const handleDelete = async (planId) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        const { error } = await supabase
          .from('subscription_plans')
          .delete()
          .eq('id', planId);

        if (error) throw error;

        await fetchPlans();
        setMessage('Plan deleted successfully!');
      } catch (err) {
        setError(err.message || 'Failed to delete plan.');
      }
    }
  };

  const formatPrice = (cents, planType = null) => {
    // Show "Custom" for Enterprise plans with zero price
    if (cents === 0 && planType === 'enterprise') {
      return 'Custom';
    }
    // Show "Free" for other plans with zero price
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'free': return Users;
      case 'trial': return Calendar;
      case 'basic': return Shield;
      case 'pro': return Crown;
      case 'enterprise': return Zap;
      default: return CreditCard;
    }
  };

  const getPlanColor = (planType) => {
    switch (planType) {
      case 'free': return 'text-gray-600';
      case 'trial': return 'text-green-600';
      case 'basic': return 'text-blue-600';
      case 'pro': return 'text-purple-600';
      case 'enterprise': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plans</h1>
          <p className="text-gray-600 mt-2">Manage subscription plans and pricing tiers</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingPlan(null);
            setShowCreateForm(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Plan
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-4 w-4 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => {
          const IconComponent = getPlanIcon(plan.plan_type);
          return (
            <div key={plan.id} className="bg-white rounded-lg border border-gray-200 p-6 relative">
              {/* Custom URL Token - Near Top */}
              {plan.unique_token && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-purple-700 mb-1">Custom Subscription URL</p>
                      <a
                        href={`/subscribe/${plan.unique_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:text-purple-700 font-mono break-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {typeof window !== 'undefined' ? window.location.origin : ''}/subscribe/{plan.unique_token}
                      </a>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/subscribe/${plan.unique_token}`;
                        navigator.clipboard.writeText(url);
                        setMessage('URL copied to clipboard!');
                        setTimeout(() => setMessage(''), 2000);
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 flex-shrink-0"
                      title="Copy URL"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <IconComponent className={`h-8 w-8 ${getPlanColor(plan.plan_type)}`} />
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-600">{plan.description}</p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="text-blue-600 hover:text-blue-700 p-1"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Monthly</span>
                  <span className="font-semibold text-lg">
                    {plan.price_monthly === 0 && plan.plan_type === 'enterprise' ? 'Custom' : formatPrice(plan.price_monthly)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Yearly</span>
                  <span className="font-semibold text-lg">
                    {plan.price_yearly === 0 && plan.plan_type === 'enterprise' ? 'Custom' : formatPrice(plan.price_yearly)}
                  </span>
                </div>
                {plan.trial_days > 0 && (
                  <div className="flex items-center text-sm text-green-600">
                    <Calendar className="h-4 w-4 mr-1" />
                    {plan.trial_days} day trial
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Features</h4>
                <ul className="space-y-1">
                  {plan.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-center">
                      <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                  {plan.features.length > 3 && (
                    <li className="text-sm text-gray-500">
                      +{plan.features.length - 3} more features
                    </li>
                  )}
                </ul>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    plan.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {plan.show_on_frontend !== false && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Visible
                    </span>
                  )}
                  {plan.show_on_frontend === false && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Hidden
                    </span>
                  )}
                  {plan.unique_token && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Custom URL
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end text-sm">
                  <span className="text-gray-500">Order: {plan.sort_order}</span>
                  {plan.unique_token && (
                    <a
                      href={`/subscribe/${plan.unique_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-xs mt-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Custom Page →
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingPlan ? 'Edit Plan' : 'Create New Plan'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingPlan(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan Type
                    </label>
                    <select
                      name="plan_type"
                      value={formData.plan_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="free">Free</option>
                      <option value="trial">Trial (14-day free trial, no card)</option>
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Price (cents)
                    </label>
                    <input
                      type="number"
                      name="price_monthly"
                      value={formData.price_monthly}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Yearly Price (cents)
                    </label>
                    <input
                      type="number"
                      name="price_yearly"
                      value={formData.price_yearly}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trial Days
                    <span className="text-xs text-gray-500 ml-2">(Not used for trial plan type - trial is a separate plan)</span>
                  </label>
                  <input
                    type="number"
                    name="trial_days"
                    value={formData.trial_days}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>

                {/* Unique Token for Custom URL - Moved up for visibility */}
                <div className="border-t border-gray-200 pt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Subscription URL (Optional)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="unique_token"
                      value={formData.unique_token || ''}
                      onChange={handleInputChange}
                      placeholder={formData.name ? `e.g., ${slugifyPlanName(formData.name)}-abc123xyz456` : "e.g., enterprise-abc123xyz456"}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateToken}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      title={formData.name ? `Generate token with plan name: ${formData.name}` : "Enter plan name first for better token"}
                    >
                      Generate
                    </button>
                  </div>
                  {formData.name && (
                    <p className="text-xs text-gray-500 mt-1">
                      Token will start with: <span className="font-mono">{slugifyPlanName(formData.name)}-</span>
                    </p>
                  )}
                  {formData.unique_token && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-700 mb-1">Permanent Subscription URL:</p>
                      <div className="flex items-center gap-2 mb-2">
                        <a
                          href={`/subscribe/${formData.unique_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-700 font-mono break-all flex-1"
                        >
                          {typeof window !== 'undefined' ? window.location.origin : ''}/subscribe/{formData.unique_token}
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/subscribe/${formData.unique_token}`;
                            navigator.clipboard.writeText(url);
                            setMessage('URL copied to clipboard!');
                            setTimeout(() => setMessage(''), 2000);
                          }}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex-shrink-0"
                          title="Copy URL"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        💡 Share this permanent URL with customers - they can click and subscribe directly
                      </p>
                    </div>
                  )}
                  {!formData.unique_token && (
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Tip: Generate a unique token to create a permanent subscription URL for this plan. This allows you to send customers a direct link to subscribe.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features
                  </label>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="flex-1">{feature}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        placeholder="Add a feature"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                      />
                      <button
                        type="button"
                        onClick={handleAddFeature}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="show_on_frontend"
                      checked={formData.show_on_frontend !== false}
                      onChange={(e) => setFormData({ ...formData, show_on_frontend: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Show on Frontend</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingPlan(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlansPage;
